// Platform detection
export const isMac = typeof navigator !== 'undefined' && 
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);

export const isMobile = typeof navigator !== 'undefined' && 
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

// Also check for touch-only devices
export const isTouchDevice = typeof window !== 'undefined' && 
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

/**
 * Format a keyboard shortcut for display based on the current platform.
 * Returns empty string on mobile devices.
 * 
 * @param key - The main key (e.g., '/', 'T', 'S')
 * @param modifiers - Optional modifiers: mod (Cmd/Ctrl), shift, alt
 */
export function formatShortcut(
  key: string, 
  modifiers?: { 
    mod?: boolean; 
    shift?: boolean; 
    alt?: boolean;
  }
): string {
  // Hide shortcuts on mobile/touch devices
  if (isMobile || isTouchDevice) return '';
  
  const parts: string[] = [];
  
  if (modifiers?.mod) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (modifiers?.shift) {
    parts.push(isMac ? '⇧' : 'Shift');
  }
  if (modifiers?.alt) {
    parts.push(isMac ? '⌥' : 'Alt');
  }
  
  // Format special keys
  const keyDisplay = key === ' ' ? 'Space' : 
                     key.toLowerCase() === 'escape' ? 'Esc' :
                     key.length === 1 ? key.toUpperCase() : key;
  parts.push(keyDisplay);
  
  return parts.join(isMac ? '' : '+');
}

/**
 * Predefined shortcuts for common actions
 */
export const SHORTCUTS = {
  aiPanel: () => formatShortcut('/', { mod: true }),
  typewriter: () => formatShortcut('T', { mod: true }),
  ambientSounds: () => formatShortcut('M', { mod: true }),
  save: () => formatShortcut('S', { mod: true }),
  history: () => formatShortcut('H', { mod: true, shift: true }),
  citations: () => formatShortcut('C', { mod: true, shift: true }),
  citationStyle: () => formatShortcut('S', { mod: true, shift: true }),
  search: () => formatShortcut('K', { mod: true }),
  escape: () => 'Esc',
} as const;

/**
 * Check if we should show keyboard shortcuts in the UI
 */
export function shouldShowShortcuts(): boolean {
  return !isMobile && !isTouchDevice;
}
